/**
 * ProductCard template
 * ------------------------------------------------------------------------------
 * Returns an HTML string for a product card using the given Nosto product
 * params. Phase one: everything visible on load. Phase two will add the
 * multivariant quickbuy form, mobile quick-add trigger, metaobject-driven
 * pill messaging, dated restock overlay and colour image swatches.
 */

const SHOPIFY_IMAGE_WIDTHS = [160, 320, 540, 720, 900]
const PLACEHOLDER_SRC = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
const OPTION_NAMES = { 1: 'Size', 2: 'Fit', 3: 'Length' }
const NAV_ICON_PREV =
  '<svg width="8" height="15" viewBox="0 0 8 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 1L1 7.5L7 14" stroke="black"/></svg>'
const NAV_ICON_NEXT =
  '<svg width="8" height="15" viewBox="0 0 8 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7.5L1 14" stroke="black"/></svg>'

function getDependencies () {
  return {
    translations: window.theme.strings,
    moneyFormat: window.theme.moneyFormat,
    formatMoney: window.BAOv2.utils.formatMoney,
    hexMap: (window.theme.props && window.theme.props.colorHexs) || {},
  }
}

function normalizeProduct (params) {
  return {
    title: params.name || '',
    handle: params.productHandle || '',
    productId: params.productId || '',
    defaultVariantId: params.skus[0].id || '',
    url: params.url || '',
    imageUrl: params.imageUrl || '',
    alternateImageUrls: params.alternateImageUrls || [],
    tags1: params.tags1 || [],
    skus: params.skus || [],
    customFields: params.customFields || [],
    price: params.price || 0,
    listPrice: params.listPrice || 0,
    media: params.media || [],
    featuredMedia: params.featuredMedia || null,
  }
}

function resolveVideoSrc (media) {
  if (!media || typeof media !== 'object') return ''

  if (typeof media.src === 'string') return media.src
  if (typeof media.url === 'string') return media.url
  if (Array.isArray(media.sources) && media.sources.length) {
    return media.sources[0]?.url || media.sources[0]?.src || ''
  }

  return ''
}

function getShopifyVideoMedia (product, position) {
  const mediaList = Array.isArray(product.media) ? product.media : []
  const fallbackMediaList = product.featuredMedia ? [product.featuredMedia] : []
  const items = mediaList.length ? mediaList : fallbackMediaList

  const videoMedia = items.find(media => {
    const mediaType = String(media?.mediaType || media?.type || '').toLowerCase()
    const mediaPosition = Number(media?.position || 1)
    return mediaType.includes('video') && mediaPosition === position
  })

  return videoMedia ? resolveVideoSrc(videoMedia) : ''
}

function renderShopifyVideoMedia (src) {
  return `<video class="prd-Card_Video-player" autoplay loop muted playsinline><source src="${src}" type="video/mp4"></video>`
}

function appendWidthParam (src, width) {
  if (!src) return ''

  const hasQuery = src.includes('?')
  return `${src}${hasQuery ? '&' : '?'}width=${width}`
}

function buildShopifySrcset (src) {
  if (!src) return ''

  return SHOPIFY_IMAGE_WIDTHS.map(width => `${appendWidthParam(src, width)} ${width}w`).join(', ')
}

function renderImage (src, alt, index, imageSizes) {
  const srcset = buildShopifySrcset(src)
  return `<img class="rsp-Image_Image" src="${PLACEHOLDER_SRC}" srcset="${srcset}" sizes="${imageSizes}" alt="${alt}" loading="${
    index < 8 ? 'eager' : 'lazy'
  }" width="900" height="1350" />`
}

function buildTooltipMarkup (tags1, translations) {
  const isDiscontinued = tags1.includes('discontinued')
  const isPreOrder = tags1.includes('pre-order')

  if (isDiscontinued) {
    return `
      <div class="prd-Card_Overlay">
        <div class="prd-Card_OverlayTooltip">${translations.discontinued_tooltip}</div>
        <p class="prd-Card_OverlayText">${translations.sold_out}</p>
      </div>`
  }

  if (isPreOrder) {
    return `
      <div class="prd-Card_Overlay">
        <div class="prd-Card_OverlayTooltip">${translations.pre_order_tooltip}</div>
        <p class="prd-Card_OverlayText">${translations.pre_order}</p>
      </div>`
  }

  return ''
}

function buildPricingState (product) {
  const isSale = product.listPrice > product.price
  const priceVaries =
    product.skus.length > 1 && !product.skus.every(sku => sku.price === product.skus[0].price)
  const priceInCents = product.price * 100
  const compareAtPriceInCents = isSale ? product.listPrice * 100 : null
  const discountPercent = isSale
    ? Math.round(((product.listPrice - product.price) * 100) / product.listPrice / 10) * 10
    : 0

  return {
    isSale,
    priceVaries,
    priceInCents,
    compareAtPriceInCents,
    discountPercent,
  }
}

function buildColorState (product, dependencies) {
  const colorsField = product.customFields.find(field => field.key.startsWith('colors-available'))
  const colorNames = colorsField
    ? colorsField.value
      .split(',')
      .map(color => (color.includes(':') ? color.split(':').pop() : color))
      .map(color => color.trim())
      .filter(Boolean)
    : []

  const resolvedColors = colorNames
    .map(name => ({ name, hex: dependencies.hexMap[name.toLowerCase()] }))
    .filter(color => color.hex)

  const maxColoursToShow = window.BAO.shoplift.activeTests.plpSwatchesTest ? 15 : 3

  return {
    visibleColors: resolvedColors.slice(0, maxColoursToShow),
    remainingColors: resolvedColors.length - maxColoursToShow,
  }
}

function buildMediaState (product, index, type) {
  const hoverAltSrc = product.alternateImageUrls[0]
  const mainVideoSrc = getShopifyVideoMedia(product, 1)
  const hoverVideoSrc = getShopifyVideoMedia(product, 2)
  let imageSizes = '(max-width: 899px) 50vw, (min-width: 900px) 25vw'
  if (type === 'autocomplete') {
    imageSizes = '(max-width: 899px) 50vw, (min-width: 900px) 33vw'
  }

  const mainImageMarkup = mainVideoSrc
    ? renderShopifyVideoMedia(mainVideoSrc)
    : renderImage(product.imageUrl, product.title, index, imageSizes)

  const hoverImageMarkup = hoverVideoSrc
    ? renderShopifyVideoMedia(hoverVideoSrc)
    : hoverAltSrc
      ? renderImage(hoverAltSrc, product.title, index, imageSizes)
      : ''

  return {
    mainImageMarkup,
    hoverSlideMarkup: hoverImageMarkup
      ? `<div class="prd-Card_Image prd-Card_Image-hidden prd-Card_CarouselImage" data-product-card-carousel-el="slide">${hoverImageMarkup}</div>`
      : '',
  }
}

function renderDiscountPillMarkup (pricingState) {
  return pricingState.discountPercent
    ? `<div class="prd-Pill"><ul class="prd-Pill_List"><li class="prd-Pill_Pill prd-Pill_Pill--discount">-${pricingState.discountPercent}%</li></ul></div>`
    : ''
}

function renderColorsMarkup (colorState, translations, productUrl) {
  return `
    <a class="prd-Card_Colors nosto" href="${productUrl}">
      ${colorState.visibleColors
        .map(
          color => `<span class="prd-Card_Color" style="background-color: ${color.hex};"></span>`,
        )
        .join('')}
      ${
        colorState.remainingColors > 0
          ? `<span class="prd-Colours_Remaining">+${colorState.remainingColors} ${translations.more}</span>`
          : ''
      }
    </a>`
}

function renderPriceMarkup (pricingState, translations, dependencies) {
  const formattedPrice = dependencies.formatMoney(
    pricingState.priceInCents,
    dependencies.moneyFormat,
  )
  const formattedCompareAt = pricingState.compareAtPriceInCents
    ? dependencies.formatMoney(pricingState.compareAtPriceInCents, dependencies.moneyFormat)
    : ''

  return `
    ${
      pricingState.isSale
        ? `<span class="util-ScreenReaderOnly">${translations.regular_price}</span><span class="prd-Price_Compare">${formattedCompareAt}</span>`
        : ''
    }
    <span class="prd-Price_Value${pricingState.isSale ? ' prd-Price_Sale' : ''}">
      ${pricingState.priceVaries ? `${translations.from} ${formattedPrice}` : formattedPrice}
    </span>`
}

function renderProductCard (state, dependencies) {
  const { product, tooltipMarkup, pricingState, colorState, mediaState, variantState } = state
  const discountPillMarkup = renderDiscountPillMarkup(pricingState)
  const colorsMarkup = renderColorsMarkup(colorState, dependencies.translations, product.url)
  const priceMarkup = renderPriceMarkup(pricingState, dependencies.translations, dependencies)
  const variantJsonMarkup = renderCardVariantJsonMarkup(
    variantState.variants,
    variantState.options.length,
  )

  return `
    <div class="prd-Card" data-product-handle="${product.handle}" data-product-id="${
    product.productId
  }" data-product-url="${product.url}">
      ${variantJsonMarkup}
      <wishlist-button
        class="prd-Card_WishlistContainer"
        data-product-id="${product.productId}"
        data-variant-id="${product.defaultVariantId}"
        data-product-url="${product.url}"
      >
        <button class="prd-Card_Wishlist prd-Card_Wishlist-add" type="button">
          <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.05 17C10.05 17 4.29198 11.6667 2.70697 9.4541C1.97461 8.43175 1.25 7.32 1.25 5.95238C1.25 3.21714 3.47678 1 6.22391 1C7.76276 1 9.13786 1.69638 10.05 2.78971C10.9621 1.69638 12.3372 1 13.8761 1C16.6232 1 18.85 3.21714 18.85 5.95238C18.85 7.32 18.0505 8.39051 17.393 9.4541C16.0253 11.6667 10.05 17 10.05 17Z" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
          </svg>
          <span class="util-ScreenReaderOnly">${dependencies.translations.add_to_wishlist}</span>
        </button>
      </wishlist-button>

      <product-card-carousel>
        <div class="prd-Card_ImageContainer prd-Card_Carousel" data-product-card-carousel-el="carousel">
          ${tooltipMarkup}
          <div class="prd-Card_Image prd-Card_CarouselImage" data-product-card-carousel-el="slide">
            ${mediaState.mainImageMarkup}
          </div>
          ${mediaState.hoverSlideMarkup}
          ${discountPillMarkup}
          <a class="prd-Card_FauxLink util-FauxLink" href="${product.url}" aria-label="${
    product.title
  }"></a>
          <div class="prd-Card_Offers" data-module="product-card-variant-button" data-module-class="js-VariantButton">
            ${renderProductOffersMarkup(product, variantState, dependencies)}
          </div>
        </div>
      </product-card-carousel>

      <div class="prd-Card_Body">
        <a class="prd-Card_Link" href="${product.url}">
          <p class="prd-Card_Title">${product.title}</p>
        </a>
        <p class="prd-Card_Price">
          ${priceMarkup}
        </p>
      </div>

      <div class="prd-Card_Footer">
        ${colorsMarkup}

        <button
          class="prd-Card_MobileQuickAddTrigger js-QuickAddToCart_Trigger"
          data-module="product-card-quick-add"
          data-module-class="js-QuickAddToCart"
          type="button"
          data-product-url="${window.theme.shopUrl}${product.url}"
        >
          <span class="util-ScreenReaderOnly">{{ 'general.accessibility.open_quick_add' | t }}</span>
          <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path stroke="#000" stroke-width="1" stroke-linejoin="round" d="M1 3.947h13.263V15H1z"/>
            <path d="M4.352 4.14C4.589 2.362 5.967 1 7.632 1c1.664 0 3.042 1.363 3.28 3.14" stroke="#000" stroke-width="1"/>
          </svg>
        </button>
      </div>
    </div>
  `
}

function escapeHtml (value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function parseVariantStructure (skus) {
  const variants = skus.map(sku => {
    const values = String(sku.name || '')
      .split(' / ')
      .map(value => value.trim())
      .filter(Boolean)

    return {
      id: sku.id,
      name: sku.name,
      values,
      available: Number(sku.inventoryLevel) > 0,
    }
  })

  const optionCount = variants.reduce((max, variant) => Math.max(max, variant.values.length), 0)
  const options = Array.from({ length: optionCount }, (_, optionIndex) => {
    const seen = new Set()
    const values = []

    variants.forEach(variant => {
      const value = variant.values[optionIndex]
      if (!value || seen.has(value)) return
      seen.add(value)
      const available = variants.some(
        candidate => candidate.values[optionIndex] === value && candidate.available,
      )
      values.push({ value, available })
    })

    return { position: optionIndex + 1, values }
  })

  return { variants, options }
}

function renderVariantIdSelectMarkup (variants, optionCount, selectedVariant) {
  return variants
    .map(variant => {
      const dataOptionAttrs = Array.from({ length: optionCount }, (_, optionIndex) => {
        const value = variant.values[optionIndex] || ''
        return ` data-option${optionIndex + 1}="${escapeHtml(value)}"`
      }).join('')

      return `<option value="${escapeHtml(variant.id)}"${dataOptionAttrs}${
        variant.id === selectedVariant.id ? ' selected="selected"' : ''
      }${variant.available ? '' : ' disabled="disabled"'}>${escapeHtml(variant.name)}</option>`
    })
    .join('')
}

function renderOptionWindowMarkup ({ option, selectedVariant, isActive }) {
  const optionId = `option${option.position}`
  const optionName = OPTION_NAMES[option.position] || `Option ${option.position}`
  const optionSlug = optionName.toLowerCase()
  const selectedValue = selectedVariant.values[option.position - 1] || ''
  const buttonsMarkup = option.values
    .map(({ value, available }) => {
      const isSelected = value === selectedValue

      return `<button class="prd-CardOffer_Button js-VariantButton_Button" type="button" data-product-variant="${escapeHtml(
        value,
      )}" aria-selected="${
        isSelected ? 'true' : 'false'
      }" data-option-id="${optionId}" data-available="${available ? 'true' : 'false'}">${escapeHtml(
        value,
      )}</button>`
    })
    .join('')
  const selectOptionsMarkup = option.values
    .map(
      ({ value }) =>
        `<option value="${escapeHtml(value)}"${
          value === selectedValue ? ' selected="selected"' : ''
        }>${escapeHtml(value)}</option>`,
    )
    .join('')

  return `<div class="prd-CardOffer_Window js-Quickbuy_Window${
    isActive ? ' prd-CardOffer_WindowActive' : ''
  }" data-multivariant-quickbuy-el="window" data-window-index="${option.position}">
    <div class="prd-CardOffer_Option js-VariantButton_Container js-VariantButton_NostoContainer" data-quickbuy-trigger>
      <label class="prd-CardOffer_OptionLabel" for="SingleOptionSelector-${option.position -
        1}">Select a ${escapeHtml(optionName)}:</label>
      <div class="prd-CardOffer_VariableList prd-CardOffer_VariableList-${escapeHtml(optionSlug)}">
        ${buttonsMarkup}
        <select class="prd-Offer_OptionSelect util-ScreenReaderOnly js-VariantButton_Select" id="SingleOptionSelector-${option.position -
          1}" data-single-option-selector data-index="${optionId}">
          ${selectOptionsMarkup}
        </select>
      </div>
    </div>
  </div>`
}

function renderCardVariantJsonMarkup (variants, optionCount) {
  if (optionCount < 2) return ''

  const payload = {
    variants: variants.map(variant => {
      const row = {
        id: variant.id,
        available: variant.available,
      }
      for (let i = 0; i < optionCount; i += 1) {
        row[`option${i + 1}`] = variant.values[i] || ''
      }
      return row
    }),
  }

  return `<script type="application/json">${JSON.stringify(payload)}</script>`
}

function renderProductOffersMarkup (product, variantState, dependencies) {
  if (!product.skus.length) {
    return ''
  }

  const { variants, options } = variantState
  const optionCount = options.length
  const selectedVariant = variants.find(variant => variant.available) || variants[0]
  const variantIdSelectMarkup = renderVariantIdSelectMarkup(variants, optionCount, selectedVariant)
  const hasMultipleOptions = optionCount >= 1 && variants.length >= 1

  const windowsMarkup = hasMultipleOptions
    ? options
      .map((option, index) =>
        renderOptionWindowMarkup({
          option,
          selectedVariant,
          isActive: index === 0,
        }),
      )
      .join('')
    : ''

  const navMarkup =
    optionCount > 1
      ? `<button class="prd-CardOffer_WindowNav prd-CardOffer_NostoWindowNav js-CardOffer_WindowNav" type="button" data-direction="prev" aria-label="Back to previous option" disabled>${NAV_ICON_PREV}</button>
        <button class="prd-CardOffer_WindowNav prd-CardOffer_NostoWindowNav js-CardOffer_WindowNav" type="button" data-direction="next" aria-label="Continue to next option">${NAV_ICON_NEXT}</button>`
      : ''

  return `
    <form
      class="prd-CardOffer_Form js-AddToCart"
      action="/cart/add"
      method="post"
      enctype="multipart/form-data"
      data-product-form
      data-product-handle="${product.handle}"
    >
      ${
        hasMultipleOptions
          ? `<div class="prd-CardOffer_Options util-Js">
              <div class="prd-CardOffer_Windows js-Quickbuy_Windows">
                ${windowsMarkup}
                ${navMarkup}
              </div>
            </div>`
          : ''
      }
      <select name="id" class="util-NoJs util-ScreenReaderOnly js-VariantButton_ID" data-product-select>
        ${variantIdSelectMarkup}
      </select>
      <div class="prd-CardOffer_Option util-ScreenReaderOnly">
        <label class="prd-CardOffer_Label" for="Quantity">${
          dependencies.translations.product_form_legend
        }</label>
        <input class="prd-CardOffer_Input" type="number" id="Quantity" name="quantity" value="1" min="1">
      </div>
      <div class="prd-CardOffer_Footer util-ScreenReaderOnly">
        <button class="prd-CardOffer_Submit js-AddToCart_Submit" type="submit" name="add" data-add-to-cart>
          <span class="prd-CardOffer_SubmitText" data-add-to-cart-text>${
            dependencies.translations.addToCart
          }</span>
        </button>
      </div>
    </form>`
}

function ProductCard (params) {
  if (!params) {
    return ''
  }

  const dependencies = getDependencies()
  const product = normalizeProduct(params.product)
  const state = {
    product,
    index: params.index,
    type: params.type,
    tooltipMarkup: buildTooltipMarkup(product.tags1, dependencies.translations),
    pricingState: buildPricingState(product),
    colorState: buildColorState(product, dependencies),
    mediaState: buildMediaState(product, params.index, params.type),
    variantState: parseVariantStructure(product.skus),
  }

  return renderProductCard(state, dependencies)
}

if (window.theme.results) {
  window.theme.results.templates.ProductCard = ProductCard
}

if (window.theme.autocomplete) {
  window.theme.autocomplete.templates.ProductCard = ProductCard
}
